// HTML sanitized
import { useTranslation } from 'react-i18next';
import {
  Box,
  chakra,
  Flex,
  HStack,
  IconButton,
  Link,
  Text,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import DOMpurify from 'dompurify';

import type { NewsPostAttachment, NewsPostLink } from '@izimi/api';
import { getExtension, getNameWithoutExtension } from '@izimi/utils';

import { FeatherIcon, FileIcon } from '../../icon';
import LinkItem from '../../Link/LinkItem';
import { NewsItemHeaderMask } from './NewsItemHeaderMask';
import { PublishedIllustration } from './PulblishedIllustration';

const Iframe = chakra('iframe');

const ImageHeader = ({ imageSrc }: { imageSrc: string }) => {
  return (
    <Box pos='relative' as='figure'>
      <Box
        h={{ base: 215, md: 430 }}
        bg={`url(${imageSrc})`}
        backgroundSize='cover'
        borderTopRadius='32px'
      />
      <Box position='absolute' bottom={0}>
        <NewsItemHeaderMask />
      </Box>
    </Box>
  );
};

const ItemHeader = ({
  title,
  publicationDate,
  relatedToCategories,
}: {
  title: string;
  publicationDate: string;
  relatedToCategories: {
    id: number;
    name: string;
  }[];
}) => {
  const { t } = useTranslation();
  return (
    <VStack alignItems='flex-start' as='header'>
      <HStack>
        <PublishedIllustration />
        <Box pl={2}>
          <Text variant='heading6' color='primary500'>
            {t('newsFeed.detail.published')}
          </Text>
          <Text variant='heading6' color='primary500'>
            {t('newsFeed.detail.dateInCategory', {
              date: publicationDate,
              category: relatedToCategories.map((cat) => cat.name).join(', '),
            })}
          </Text>
        </Box>
      </HStack>
      <Text variant='heading1' as='h1' textAlign='left' py={4}>
        {title}
      </Text>
    </VStack>
  );
};

const ItemContent = ({ html }: { html: string }) => {
  return (
    <Box
      my={4}
      dangerouslySetInnerHTML={{ __html: DOMpurify.sanitize(html) }}
      className='external-content'
    />
  );
};

const ItemVideo = ({ title, video }: { title: string; video: string }) => {
  return (
    <Iframe
      my={10}
      title={title}
      frameBorder='0'
      scrolling='no'
      marginHeight={0}
      marginWidth={0}
      sx={{ aspectRatio: '16/9' }}
      width='100%'
      src={`https://www.youtube.com/embed/${video}`}
    />
  );
};

const ItemAttachments = ({ attachments }: { attachments: NewsPostAttachment[] }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <VStack
      boxShadow='0px 2px 6px rgba(61, 56, 54, 0.08)'
      borderRadius='12px'
      border='1px solid #F3F0EE;'
      my={2}
      p={4}>
      {attachments.map((attachment) => (
        <Link href={attachment.url} isExternal my={2} w='100%' key={attachment.originalFilename}>
          <HStack>
            <Flex shrink={0} mx={4}>
              <FileIcon
                color={theme.colors.primary400}
                extension={getExtension(attachment.originalFilename)}
              />
            </Flex>
            <Text variant='heading6' color='primary500' width='100%'>
              {getNameWithoutExtension(attachment.originalFilename)}
            </Text>
            <IconButton
              aria-label={t('action.download')}
              icon={<FeatherIcon name='arrow-right' color='primary500' />}
            />
          </HStack>
        </Link>
      ))}
    </VStack>
  );
};

const ItemLinks = ({ links }: { links: NewsPostLink[] }) => {
  return (
    <LinkItem.Wrapper>
      {links.map((link) => (
        <LinkItem.Inner
          href={link.url}
          isExternal={link.newWindow}
          key={link.title}
          title={link.title}
        />
      ))}
    </LinkItem.Wrapper>
  );
};

const NewsItem = {
  ImageHeader,
  Header: ItemHeader,
  Content: ItemContent,
  Video: ItemVideo,
  Attachments: ItemAttachments,
  Links: ItemLinks,
};

export default NewsItem;
